import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, MicOff, Trash2, Ban, CheckCircle } from "lucide-react";
import { API, AuthContext } from "../App";

/**
 * Voice IP Bans manager — admin-only.
 * Banned IPs can still chat normally, only their `voice_join` WS event is blocked.
 * Bans support duration (in hours) or are permanent.
 */
const VoiceBansManager = () => {
  const { token } = useContext(AuthContext);
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [target, setTarget] = useState(""); // ip or username
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState("permanent"); // permanent | 24 | 168 | 720

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/voice-bans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBans(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load voice bans", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const t = target.trim();
    if (!t) {
      toast.error("Enter a username or IP address");
      return;
    }
    const isIP = /^[0-9.:a-fA-F]+$/.test(t) && (t.includes(".") || t.includes(":"));
    const body = {
      reason: reason.trim() || undefined,
      duration_hours: duration === "permanent" ? null : Number(duration),
    };
    if (isIP) body.ip = t;
    else body.username = t;

    try {
      await axios.post(`${API}/admin/voice-bans`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Voice ban added for ${t}`);
      setTarget(""); setReason(""); setDuration("permanent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add voice ban");
    }
  };

  const handleRemove = async (ip) => {
    if (!window.confirm(`Lift voice ban for ${ip}?`)) return;
    try {
      await axios.delete(`${API}/admin/voice-bans/${encodeURIComponent(ip)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Voice ban lifted for ${ip}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to lift ban");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2" data-testid="voice-bans-panel">
      <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
        <MicOff className="w-4 h-4" />
        Voice Chat IP Bans
      </h3>
      <p className="text-[11px] text-zinc-600 mb-3">
        These IPs can still chat & enter rooms — they just can't use the microphone.
        Useful for moderating mic abuse without an account-wide ban.
      </p>

      {/* Create ban form */}
      <form
        onSubmit={handleCreate}
        className="bg-red-50 border border-red-200 rounded p-2 mb-3 space-y-2"
        data-testid="voice-ban-create-form"
      >
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="font-arial text-xs text-[#808080]">Username OR IP address</label>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="retro-input w-full text-xs"
              placeholder="e.g. badmic_dan  OR  192.168.1.50"
              data-testid="voice-ban-target-input"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="font-arial text-xs text-[#808080]">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="retro-input w-full text-xs"
              placeholder="e.g. Music blasting / harassment"
              data-testid="voice-ban-reason-input"
            />
          </div>
          <div>
            <label className="font-arial text-xs text-[#808080]">Duration</label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="retro-input text-xs"
              data-testid="voice-ban-duration-select"
            >
              <option value="24">24 hours</option>
              <option value="168">7 days</option>
              <option value="720">30 days</option>
              <option value="permanent">Permanent</option>
            </select>
          </div>
          <button
            type="submit"
            className="retro-button px-3 py-1 bg-red-100 flex items-center gap-1 text-xs font-bold"
            data-testid="voice-ban-submit-btn"
          >
            <Ban className="w-3 h-3 text-red-600" /> Ban from Voice
          </button>
        </div>
      </form>

      {loading ? (
        <div className="text-center py-6 text-[#808080] text-xs">Loading…</div>
      ) : bans.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
          <p className="font-arial text-sm text-[#808080]">No active voice bans</p>
        </div>
      ) : (
        <table className="w-full text-xs" data-testid="voice-bans-table">
          <thead className="bg-[#800000] text-white sticky top-0">
            <tr>
              <th className="p-2 text-left">IP Address</th>
              <th className="p-2 text-left">User</th>
              <th className="p-2 text-left">Reason</th>
              <th className="p-2 text-left">Banned By</th>
              <th className="p-2 text-left">Started</th>
              <th className="p-2 text-left">Expires</th>
              <th className="p-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {bans.map((b, i) => (
              <tr key={`${b.ip}-${i}`} className="border-b border-[#E0E0E0] hover:bg-[#FFE0E0]">
                <td className="p-2 font-mono font-bold">{b.ip}</td>
                <td className="p-2 text-[#000080]">{b.associated_user?.username || "—"}</td>
                <td className="p-2">{b.reason || "—"}</td>
                <td className="p-2">{b.banned_by}</td>
                <td className="p-2 text-[#808080]">{formatDate(b.banned_at)}</td>
                <td className="p-2 text-[#808080]">{b.expires_at ? formatDate(b.expires_at) : <span className="text-red-600 font-bold">Permanent</span>}</td>
                <td className="p-2 text-center">
                  <button
                    className="retro-button px-2 py-0.5 bg-green-100 text-xs flex items-center gap-1 mx-auto"
                    onClick={() => handleRemove(b.ip)}
                    data-testid={`voice-ban-unban-${b.ip}`}
                  >
                    <Mic className="w-3 h-3" /> Unban
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default VoiceBansManager;
